import { Component, Input, computed, inject, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import type { ResolveFn } from '@angular/router'
import { ActivatedRoute } from '@angular/router'

import { TranslatePipe } from '@ngx-translate/core'
import { InfiniteScrollDirective } from 'ngx-infinite-scroll'
import { catchError, forkJoin, map, of, take } from 'rxjs'

import { ArticlesService } from '../../../api/articles-service/articles.service'
import { AuthorsService } from '../../../api/authors-service/authors.service'
import type {
  ArticlesDto,
  AuthorDto,
  MagazineDto,
} from '../../../api/responses'
import { ArticleList } from '../../../components/article-list/article-list'
import { BreadcrumbsComponent } from '../../../components/breadcrumbs/breadcrumbs.component'
import { OurSubscriptionsComponent } from '../../../pages/home/components/our-subscriptions/our-subscriptions.component'
import { AuthService } from '../../../services/auth/auth.service'
import { FormatAuthorNamePipe } from '../../../shared/pipes/format-author-name.pipe'
import type { SeoRouteMeta } from '../../../shared/seo/seo.types'
import { firstText } from '../../../shared/seo/seo.util'
import type {
  ArticleWithNullableRefs,
  BreadcrumbItem,
  TranslationParams,
} from '../../../types/types'
import { MainInfoComponent } from './components/main-info/main-info.component'

const ARTICLES_PER_PAGE = 20

export interface AuthorDetailData {
  person: AuthorDto | null
  articles: ArticlesDto[]
  totalArticles: number
  /** Translation key of the message to show instead of the page. */
  error: string | null
}

// Fetched in a resolver so the server render waits for it; see
// magazineDetailResolver. Later pages still load on scroll.
export const authorDetailResolver: ResolveFn<AuthorDetailData> = route => {
  const authorsService = inject(AuthorsService)
  const articlesService = inject(ArticlesService)
  const authService = inject(AuthService)

  const personId = Number(route.paramMap.get('id'))
  if (!personId || isNaN(personId)) {
    return of({
      person: null,
      articles: [],
      totalArticles: 0,
      error: 'pages.authors.errors.invalidId',
    })
  }

  // The server renders anonymously, so a logged-in reader needs their own
  // copy of the articles for `magazine.manual_url` (category buttons).
  const articlesPolicy = authService.isAuthenticated()
    ? 'network-only'
    : 'cache-first'

  return forkJoin({
    person: authorsService.getAuthorById(personId).pipe(take(1)),
    firstPage: articlesService
      .getArticlesByAuthorId(personId, 1, ARTICLES_PER_PAGE, articlesPolicy)
      .pipe(take(1)),
  }).pipe(
    map(({ person, firstPage }) => ({
      person: person ?? null,
      articles: firstPage.articles,
      totalArticles: firstPage.total,
      error: person ? null : 'pages.authors.errors.notFound',
    })),
    catchError((error: unknown) => {
      console.error('Error fetching author details:', error)
      return of({
        person: null,
        articles: [],
        totalArticles: 0,
        error: 'pages.authors.errors.failedToLoad',
      })
    })
  )
}

/**
 * Head metadata for an author or editor, registered as `data: { seo: authorSeo
 * }`.
 */
export const authorSeo: SeoRouteMeta = route => {
  const detail = route.data['authorDetail'] as AuthorDetailData | undefined
  const person = detail?.person
  if (!person) return null

  return {
    title: new FormatAuthorNamePipe().transform(person),
    description: firstText(
      person.about,
      detail.articles.map(article => article.title).join(' · ')
    ),
    // An editor is the same person as the author with that id.
    canonicalPath: `/authors/${String(person.id)}`,
  }
}

@Component({
  selector: 'dhp-author-details',
  templateUrl: './author-details.component.html',
  styleUrls: ['./author-details.component.scss'],
  providers: [FormatAuthorNamePipe],
  imports: [
    BreadcrumbsComponent,
    MainInfoComponent,
    InfiniteScrollDirective,
    ArticleList,
    OurSubscriptionsComponent,
    TranslatePipe,
  ],
})
export class AuthorDetailsComponent {
  // Inputs for reusability
  // TODO: Skipped for migration because:
  //  Your application code writes to the input. This prevents migration.
  @Input() personId!: number
  // TODO: Skipped for migration because:
  //  Your application code writes to the input. This prevents migration.
  @Input() personType!: 'author' | 'editor'

  // Signals for state management
  person = signal<AuthorDto | null>(null)
  articles = signal<ArticlesDto[]>([])
  totalArticles = signal<number>(0)
  isLoadingMore = signal<boolean>(false)
  hasMoreItems = signal<boolean>(true)

  error = signal<string | null>(null)
  breadcrumbItems = signal<BreadcrumbItem[]>([])

  // This is really ugly, but to be solved later
  adaptedArticles = computed(() =>
    this.articles().map(article => {
      const mappedArticle: ArticleWithNullableRefs = {
        ...article,
        magazine: article.magazine
          ? ({
              ...article.magazine,
              title: article.magazine.title ?? undefined,
            } as MagazineDto & { title: string | null })
          : null,
        edition: article.edition
          ? {
              ...article.edition,
              edition_version: article.edition.edition_version ?? null,
            }
          : null,
        users: {
          authors: article?.users?.authors?.map(author => ({
            ...author,
            linkType: author.type ?? 'author',
            id: author.id || 0, // Ensure id is always a number
          })),
        },
      }
      return mappedArticle
    })
  )

  // Pagination settings; the resolver already fetched page 1
  private readonly itemsPerPage = ARTICLES_PER_PAGE
  private currentPage = signal<number>(2)

  // Inject services
  private readonly articlesService = inject(ArticlesService)
  private readonly formatAuthorNamePipe = inject(FormatAuthorNamePipe)
  private readonly route = inject(ActivatedRoute)

  constructor() {
    this.route.data.pipe(takeUntilDestroyed()).subscribe(data => {
      this.personId = Number(this.route.snapshot.paramMap.get('id'))
      this.personType = data['type'] as 'author' | 'editor'
      this.applyAuthorDetail(data['authorDetail'] as AuthorDetailData)
    })
  }

  onScroll(): void {
    this.fetchArticles()
  }

  private applyAuthorDetail({
    person,
    articles,
    totalArticles,
    error,
  }: AuthorDetailData): void {
    this.error.set(error)
    this.person.set(person)
    this.articles.set(articles)
    this.totalArticles.set(totalArticles)
    this.hasMoreItems.set(articles.length >= this.itemsPerPage)
    this.currentPage.set(2)
    this.updateBreadcrumbs()
  }

  private fetchArticles(): void {
    if (this.isLoadingMore() || !this.hasMoreItems()) return

    this.isLoadingMore.set(true)

    this.articlesService
      .getArticlesByAuthorId(
        this.personId,
        this.currentPage(),
        this.itemsPerPage
      )
      .subscribe({
        next: ({ articles: newArticles, total }) => {
          this.totalArticles.set(total)
          this.articles.set([...this.articles(), ...newArticles])

          if (newArticles.length < this.itemsPerPage) {
            this.hasMoreItems.set(false)
          }

          this.currentPage.set(this.currentPage() + 1)
          this.isLoadingMore.set(false)
        },
        error: () => {
          this.setError('pages.authors.errors.failedToLoad')
          this.isLoadingMore.set(false)
        },
      })
  }

  private updateBreadcrumbs(): void {
    const person = this.person()
    if (!person) {
      this.breadcrumbItems.set([])
      return
    }

    const baseRoute = this.personType === 'author' ? '/authors' : '/editors'

    this.breadcrumbItems.set([
      {
        label:
          this.personType === 'author'
            ? 'pages.authors.authors'
            : 'pages.editors.editors',
        url: baseRoute,
      },
      {
        label: this.formatAuthorNamePipe.transform(person),
        url: `${baseRoute}/${person.id}`,
      },
    ])
  }

  private setError(message: string, _params?: TranslationParams): void {
    this.error.set(message)
  }
}
